import { useOriginalEstimateField } from '@atlassian/jira-issue-field-original-estimate/src/services';
import { TIME_ESTIMATE_TYPE } from '@atlassian/jira-platform-field-config';
import { useGenericClipboardHandler } from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';
import { timeTrackingSupportedTypes } from '../supported-field-types';
import type { TransformedTimeOriginalEstimate } from '../types';

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): number | null => {
	if (!clipboardItem.plainText || !metadata?.fieldType) {
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	if (!(timeTrackingSupportedTypes as string[]).includes(metadata.fieldType)) {
		return null;
	}

	const time = Number(clipboardItem.plainText);

	if (Number.isNaN(time)) {
		return null;
	}

	return time;
};

const useTimeTrackingClipboardHandler: PasteHandler = (
	issue: { key: string; issueId: string },
	field: ListIssueFieldTransformed,
) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const fieldValue = field.value as TransformedTimeOriginalEstimate | null;
	const hookInitialValue =
		(fieldValue?.timeAsString &&
			fieldValue?.timeInSeconds && {
				originalEstimate: fieldValue.timeAsString,
				originalEstimateSeconds: fieldValue.timeInSeconds,
			}) ||
		{};

	const [, { saveValue }] = useOriginalEstimateField({
		issueKey: issue.key,
		issueId: issue.issueId,
		fieldId: field.fieldId,
		timeTrackingValue: hookInitialValue,
	});

	return useGenericClipboardHandler(field, getPastedValue, saveValue);
};

const TimeTrackingClipboardHelper = createClipboardHelperComponent(useTimeTrackingClipboardHandler);

export const timeTrackingClipboardHandler = {
	[TIME_ESTIMATE_TYPE]: TimeTrackingClipboardHelper,
};
